import AddCircleIcon from "@mui/icons-material/AddCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { ProgressButton } from "@qubit/autoparts";
import { useTranslation } from "react-i18next";

type Props = {
  ports: number[];
  unusedPorts: number[];
  addPort: () => void;
  updatePort: (index: number, value: number) => void;
  removePort: (index: number) => void;
  hideLabel?: boolean;
  variant?: "standard" | "outlined";
  selectSx?: SxProps<Theme>;
};

export function AutostorePortSelector({
  ports,
  unusedPorts,
  addPort,
  updatePort,
  removePort,
  hideLabel = false,
  variant = "standard",
  selectSx
}: Props) {
  const { t } = useTranslation();
  const hasAssignablePortRemaining = unusedPorts.some(
    (p) => !ports.includes(p)
  );
  const showAddPortButton = ports.length < 3 && hasAssignablePortRemaining;
  const outlined = variant === "outlined";

  return (
    <Box>
      <Stack direction="column" spacing={2}>
        {ports.map((portId, index) => (
          <Box key={index}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  flex: "1 1 auto",
                  minWidth: 0,
                  width: outlined ? "100%" : "66.66%"
                }}
              >
                {!hideLabel && <InputLabel shrink>{t("port")}</InputLabel>}
                {outlined ? (
                  <FormControl fullWidth size="small">
                    <Select
                      fullWidth
                      value={portId.toString()}
                      onChange={(event) =>
                        updatePort(index, parseInt(event.target.value, 10))
                      }
                      variant="outlined"
                      sx={selectSx}
                      SelectDisplayProps={{
                        "aria-label": "autostore port selector"
                      }}
                    >
                      {unusedPorts
                        .filter(
                          (port) => !ports.includes(port) || port === portId
                        )
                        .sort((a, b) => a - b)
                        .map((port) => (
                          <MenuItem key={port} value={String(port)}>
                            {`Port ${port}`}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Select
                    fullWidth
                    value={portId.toString()}
                    onChange={(event) =>
                      updatePort(index, parseInt(event.target.value, 10))
                    }
                    variant="standard"
                    SelectDisplayProps={{
                      "aria-label": "autostore port selector"
                    }}
                  >
                    {unusedPorts
                      .filter(
                        (port) => !ports.includes(port) || port === portId
                      )
                      .sort((a, b) => a - b)
                      .map((port) => (
                        <MenuItem key={port} value={String(port)}>
                          {`Port ${port}`}
                        </MenuItem>
                      ))}
                  </Select>
                )}
              </Box>
              <Box
                sx={{
                  flexShrink: 0,
                  width: outlined ? "auto" : "33.33%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <IconButton
                  aria-label={`Remove port ${index}`}
                  onClick={() => removePort(index)}
                  disabled={ports.length <= 1}
                  sx={outlined ? { mt: 0 } : { mt: 2 }}
                >
                  <CancelIcon />
                </IconButton>
              </Box>
            </Stack>
          </Box>
        ))}
        <Box>
          {showAddPortButton && (
            <ProgressButton
              onClick={addPort}
              disabled={!hasAssignablePortRemaining}
            >
              <AddCircleIcon />
              <Typography sx={{ ml: 1 }}>{t("add port")}</Typography>
            </ProgressButton>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
